import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  SimpleGrid,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Text,
  Flex,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useCreateVenue } from 'queries/venue.query'

export function CreateVenueModal({ isOpen, onClose }) {
  const inputColor = useColorModeValue('gray.800', 'white')
  const toast = useToast()
  const createVenueMutation = useCreateVenue()
  const [gates, setGates] = useState(['Main Entrance'])
  const [newGateInput, setNewGateInput] = useState('')

  const handleAddGate = () => {
    const trimmed = newGateInput.trim()
    if (!trimmed) return
    if (gates.some(g => (typeof g === 'object' ? g.name : g).toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: 'Gate already added', status: 'warning', duration: 2000 })
      return
    }
    setGates([...gates, trimmed])
    setNewGateInput('')
  }

  const handleRemoveGate = (indexToRemove) => {
    if (gates.length <= 1) {
      toast({ title: 'At least one entrance gate is recommended', status: 'info', duration: 2000 })
    }
    setGates(gates.filter((_, idx) => idx !== indexToRemove))
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      city: '',
      address: '',
      postal_code: '',
      country: 'Germany',
      total_capacity: 500,
      parking_info: '',
      description: '',
    },
  })

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        gates: gates.length > 0 ? gates : ['Main Entrance'],
      }
      await createVenueMutation.mutateAsync(payload)
      toast({
        title: 'Venue created',
        description: 'New venue added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      reset()
      setGates(['Main Entrance'])
      setNewGateInput('')
      onClose()
    } catch (err) {
      toast({
        title: 'Failed to create venue',
        description: err?.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>Create New Venue</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
              <FormControl isRequired isInvalid={errors.name}>
                <FormLabel>Venue Name</FormLabel>
                <Input
                  color={inputColor}
                  placeholder="e.g. Bürgerhaus Dietzenbach"
                  {...register('name', { required: 'Name is required' })}
                />
              </FormControl>

              <FormControl isRequired isInvalid={errors.city}>
                <FormLabel>City</FormLabel>
                <Input
                  color={inputColor}
                  placeholder="e.g. Dietzenbach / Frankfurt"
                  {...register('city', { required: 'City is required' })}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} width="100%">
              <FormControl isRequired isInvalid={errors.address} gridColumn={{ md: 'span 2' }}>
                <FormLabel>Street Address</FormLabel>
                <Input
                  color={inputColor}
                  placeholder="e.g. Europaplatz 3"
                  {...register('address', { required: 'Address is required' })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Postal Code</FormLabel>
                <Input
                  color={inputColor}
                  placeholder="63128"
                  {...register('postal_code')}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%">
              <FormControl>
                <FormLabel>Country</FormLabel>
                <Input
                  color={inputColor}
                  defaultValue="Germany"
                  {...register('country')}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Total Capacity</FormLabel>
                <Input
                  type="number"
                  color={inputColor}
                  placeholder="1200"
                  {...register('total_capacity', { valueAsNumber: true })}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>Parking Information (Optional)</FormLabel>
              <Textarea
                color={inputColor}
                placeholder="Underground garage parking with 250 spots..."
                rows={2}
                {...register('parking_info')}
              />
            </FormControl>

            {/* Dynamic Gates Management */}
            <FormControl>
              <FormLabel>Entrance Gates / Doors (گیت‌های ورودی و خروجی)</FormLabel>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Define physical entry and exit doors for this venue (e.g. Main Entrance, VIP Gate, East Door).
              </Text>
              <Flex gap={2} mb={2}>
                <Input
                  size="sm"
                  color={inputColor}
                  placeholder="e.g. VIP Entrance, Gate A, Main Door"
                  value={newGateInput}
                  onChange={(e) => setNewGateInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddGate()
                    }
                  }}
                />
                <Button size="sm" colorScheme="brand" onClick={handleAddGate} px={4}>
                  Add Gate
                </Button>
              </Flex>
              <Wrap spacing={2}>
                {gates.map((g, idx) => {
                  const name = typeof g === 'object' ? g.name : g
                  return (
                    <WrapItem key={idx}>
                      <Tag size="md" borderRadius="full" variant="subtle" colorScheme="teal">
                        <TagLabel fontWeight="bold">{name}</TagLabel>
                        <TagCloseButton onClick={() => handleRemoveGate(idx)} />
                      </Tag>
                    </WrapItem>
                  )
                })}
              </Wrap>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                color={inputColor}
                placeholder="Acoustics, facilities, public transport accessibility..."
                rows={2}
                {...register('description')}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="brand" type="submit" isLoading={isSubmitting || createVenueMutation.isPending}>
            Save Venue
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
